import { client } from './amplifyClient';
import { appsyncPublicGraphqlRequest } from './appsyncPublicGraphql';
import { findBestUserRowByEmail } from './resolveAppUser';

export type TherapistReferralStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'CANCELLED';

export type TherapistReferralRow = {
  id: string;
  kidProfileId: string;
  therapistId: string;
  therapist2Id: string;
  parentId?: string | null;
  note?: string | null;
  organizationId?: string | null;
  status?: TherapistReferralStatus | null;
  createdAt?: string | null;
};

export type TherapistOption = {
  id: string;
  label: string;
  email?: string | null;
};

export type TherapistLookupResult =
  | { ok: true; therapist: TherapistOption }
  | { ok: false; reason: string };

type AmplifyListResp<T> = {
  data?: Array<T | null> | null;
  errors?: unknown;
  nextToken?: string | null;
};

const REFERRAL_SELECTION = `
  id
  kidProfileId
  therapistId
  therapist2Id
  parentId
  note
  organizationId
  status
  createdAt
`;

function assertNoErrors(label: string, errors: unknown) {
  if (!errors) return;
  const list = Array.isArray(errors) ? errors : [errors];
  if (list.length === 0) return;
  const msg = list
    .map((e) => (e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : String(e)))
    .filter(Boolean)
    .join('; ');
  throw new Error(msg || `${label} failed`);
}

function mapReferral(raw: Partial<TherapistReferralRow> | null | undefined): TherapistReferralRow | null {
  if (!raw?.id || !raw.kidProfileId || !raw.therapistId || !raw.therapist2Id) return null;
  return {
    id: String(raw.id),
    kidProfileId: String(raw.kidProfileId),
    therapistId: String(raw.therapistId),
    therapist2Id: String(raw.therapist2Id),
    parentId: raw.parentId ?? null,
    note: raw.note ?? null,
    organizationId: raw.organizationId ?? null,
    status: (raw.status as TherapistReferralStatus | null) ?? null,
    createdAt: raw.createdAt ?? null,
  };
}

async function listAllByFilter<T extends { id?: string | null }>(
  listFn: (args: Record<string, unknown>, opts: { authMode: string }) => Promise<AmplifyListResp<T>>,
  filter: Record<string, unknown>,
): Promise<T[]> {
  const out: T[] = [];
  let nextToken: string | undefined;
  do {
    const resp = await listFn({ filter, limit: 100, nextToken }, { authMode: 'apiKey' });
    assertNoErrors('list', resp.errors);
    for (const row of resp.data || []) {
      if (row?.id) out.push(row);
    }
    nextToken = resp.nextToken ?? undefined;
  } while (nextToken);
  return out;
}

/** List TherapistReferral via AppSync (works even when amplify_outputs lacks model introspection). */
async function listReferralsGraphql(filter: Record<string, unknown>): Promise<TherapistReferralRow[]> {
  const out: TherapistReferralRow[] = [];
  let nextToken: string | null = null;
  do {
    const data = await appsyncPublicGraphqlRequest<{
      listTherapistReferrals?: {
        items?: Array<Partial<TherapistReferralRow> | null> | null;
        nextToken?: string | null;
      };
    }>(
      `query ListTherapistReferrals($filter: ModelTherapistReferralFilterInput, $limit: Int, $nextToken: String) {
        listTherapistReferrals(filter: $filter, limit: $limit, nextToken: $nextToken) {
          items { ${REFERRAL_SELECTION} }
          nextToken
        }
      }`,
      { filter, limit: 100, nextToken },
    );
    for (const item of data.listTherapistReferrals?.items || []) {
      const mapped = mapReferral(item);
      if (mapped) out.push(mapped);
    }
    nextToken = data.listTherapistReferrals?.nextToken ?? null;
  } while (nextToken);
  return out;
}

async function getReferralGraphql(id: string): Promise<TherapistReferralRow | null> {
  const data = await appsyncPublicGraphqlRequest<{
    getTherapistReferral?: Partial<TherapistReferralRow> | null;
  }>(
    `query GetTherapistReferral($id: ID!) {
      getTherapistReferral(id: $id) { ${REFERRAL_SELECTION} }
    }`,
    { id },
  );
  return mapReferral(data.getTherapistReferral);
}

async function createReferralGraphql(input: {
  kidProfileId: string;
  therapistId: string;
  therapist2Id: string;
  parentId: string;
  note: string;
  organizationId?: string;
  status: TherapistReferralStatus;
}): Promise<TherapistReferralRow> {
  const data = await appsyncPublicGraphqlRequest<{
    createTherapistReferral?: Partial<TherapistReferralRow> | null;
  }>(
    `mutation CreateTherapistReferral($input: CreateTherapistReferralInput!) {
      createTherapistReferral(input: $input) { ${REFERRAL_SELECTION} }
    }`,
    { input },
  );
  const mapped = mapReferral(data.createTherapistReferral);
  if (!mapped) throw new Error('Referral was not created.');
  return mapped;
}

async function updateReferralGraphql(
  id: string,
  status: TherapistReferralStatus,
): Promise<TherapistReferralRow> {
  const data = await appsyncPublicGraphqlRequest<{
    updateTherapistReferral?: Partial<TherapistReferralRow> | null;
  }>(
    `mutation UpdateTherapistReferral($input: UpdateTherapistReferralInput!) {
      updateTherapistReferral(input: $input) { ${REFERRAL_SELECTION} }
    }`,
    { input: { id, status } },
  );
  const mapped = mapReferral(data.updateTherapistReferral);
  if (!mapped) throw new Error('Referral update failed.');
  return mapped;
}

function explainGraphqlMissing(err: unknown): Error {
  const msg = err instanceof Error ? err.message : String(err);
  if (/TherapistReferral|Unknown type|not present|Cannot query field/i.test(msg)) {
    return new Error(
      'TherapistReferral is not on the live backend yet. Wait for the TinyWins Amplify backend deploy to finish, then retry.',
    );
  }
  return err instanceof Error ? err : new Error(msg);
}

export async function listOrgTherapists(organizationId: string, excludeUserId?: string): Promise<TherapistOption[]> {
  const rows = await listAllByFilter<{
    id?: string | null;
    fName?: string | null;
    lName?: string | null;
    email?: string | null;
    role?: string | null;
  }>(client.models.User.list as any, { organizationId: { eq: organizationId } });

  const therapyRoles = new Set(['THERAPIST', 'CLINICIAN', 'INTERNS', 'ORG_ADMIN']);
  return rows
    .filter((u) => u.id && u.id !== excludeUserId && u.role && therapyRoles.has(String(u.role)))
    .map((u) => ({
      id: String(u.id),
      label: `${u.fName || ''} ${u.lName || ''}`.trim() || u.email || String(u.id),
      email: u.email ?? null,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

export async function resolveTherapistByEmail(
  email: string,
  excludeUserId?: string,
): Promise<TherapistLookupResult> {
  const trimmed = email.trim();
  const normalized = trimmed.toLowerCase();
  if (!normalized) return { ok: false, reason: 'Enter a therapist email.' };

  type UserRow = {
    id?: string | null;
    fName?: string | null;
    lName?: string | null;
    email?: string | null;
    role?: string | null;
  };

  const byId = new Map<string, UserRow>();

  for (const variant of Array.from(new Set([trimmed, normalized]))) {
    try {
      const rows = await listAllByFilter<UserRow>(client.models.User.list as any, {
        email: { eq: variant },
      });
      for (const u of rows) {
        if (u.id) byId.set(String(u.id), u);
      }
    } catch {
      /* fall through */
    }
  }

  if (byId.size === 0) {
    try {
      const best = await findBestUserRowByEmail(client, normalized);
      if (best?.id) {
        const full = await client.models.User.get({ id: best.id }, { authMode: 'apiKey' });
        if (full.data?.id) byId.set(String(full.data.id), full.data as UserRow);
        else byId.set(best.id, best as UserRow);
      }
    } catch {
      /* fall through */
    }
  }

  const matches = Array.from(byId.values());
  if (matches.length === 0) {
    return {
      ok: false,
      reason: 'No account found with that email. They need a TinyWins/MiraPlus therapist login first.',
    };
  }

  const therapyRoles = new Set(['THERAPIST', 'CLINICIAN', 'INTERNS', 'ORG_ADMIN']);
  const selfHit = Boolean(excludeUserId && matches.some((u) => u.id === excludeUserId));
  const clinical = matches.find(
    (u) => u.id && u.id !== excludeUserId && u.role && therapyRoles.has(String(u.role)),
  );

  if (clinical?.id) {
    return {
      ok: true,
      therapist: {
        id: String(clinical.id),
        label: `${clinical.fName || ''} ${clinical.lName || ''}`.trim() || clinical.email || String(clinical.id),
        email: clinical.email ?? null,
      },
    };
  }

  if (selfHit) {
    return {
      ok: false,
      reason:
        'That email is your own therapist account. Refer a different therapist (for example another THERAPIST email from Data Manager).',
    };
  }

  const role = String(matches[0]?.role || 'UNKNOWN');
  return {
    ok: false,
    reason: `That email is registered as ${role}, not as a therapist. Ask for a therapist account email (role THERAPIST / CLINICIAN).`,
  };
}

/** @deprecated Prefer resolveTherapistByEmail */
export async function findTherapistByEmail(email: string, excludeUserId?: string): Promise<TherapistOption | null> {
  const result = await resolveTherapistByEmail(email, excludeUserId);
  return result.ok ? result.therapist : null;
}

export async function getTherapistReferral(id: string): Promise<TherapistReferralRow | null> {
  try {
    return await getReferralGraphql(id);
  } catch (e) {
    throw explainGraphqlMissing(e);
  }
}

export async function createTherapistReferral(input: {
  kidProfileId: string;
  therapistId: string;
  therapist2Id: string;
  parentId?: string | null;
  note: string;
  organizationId?: string | null;
}): Promise<TherapistReferralRow> {
  const note = input.note.trim();
  if (!note) throw new Error('A referral note is required.');
  if (!input.therapist2Id || input.therapist2Id === input.therapistId) {
    throw new Error('Pick a different therapist to refer to.');
  }

  let parentId = input.parentId || undefined;
  if (!parentId) {
    try {
      const kidResp = await client.models.KidProfile.get({ id: input.kidProfileId }, { authMode: 'apiKey' });
      parentId = kidResp.data?.parentId || undefined;
    } catch {
      /* optional */
    }
  }
  if (!parentId) {
    throw new Error('This child has no linked parent yet. Link a parent before referring another therapist.');
  }

  try {
    const pending = await listReferralsGraphql({
      and: [
        { kidProfileId: { eq: input.kidProfileId } },
        { therapist2Id: { eq: input.therapist2Id } },
        { status: { eq: 'PENDING' } },
      ],
    });
    if (pending.length > 0) {
      throw new Error('A pending referral to this therapist already exists for this child.');
    }

    const created = await createReferralGraphql({
      kidProfileId: input.kidProfileId,
      therapistId: input.therapistId,
      therapist2Id: input.therapist2Id,
      parentId,
      note,
      organizationId: input.organizationId || undefined,
      status: 'PENDING',
    });

    // Parent inbox notification (no schema enum change — use MESSAGE_RECEIVED + payload.kind)
    try {
      const { createNotification } = await import('./notifications');
      let fromName = 'A therapist';
      let toName = 'another therapist';
      let kidName = 'your child';
      try {
        const [fromUser, toUser, kid] = await Promise.all([
          client.models.User.get({ id: input.therapistId }, { authMode: 'apiKey' }),
          client.models.User.get({ id: input.therapist2Id }, { authMode: 'apiKey' }),
          client.models.KidProfile.get({ id: input.kidProfileId }, { authMode: 'apiKey' }),
        ]);
        fromName =
          `${fromUser.data?.fName || ''} ${fromUser.data?.lName || ''}`.trim() ||
          fromUser.data?.email ||
          fromName;
        toName =
          `${toUser.data?.fName || ''} ${toUser.data?.lName || ''}`.trim() || toUser.data?.email || toName;
        kidName = (kid.data?.name || '').trim() || kidName;
      } catch {
        /* labels optional */
      }
      await createNotification({
        toUserId: parentId,
        fromUserId: input.therapistId,
        organizationId: input.organizationId,
        kidProfileId: input.kidProfileId,
        notificationType: 'THERAPIST_REFERRAL',
        title: 'Therapist referral',
        body: `${fromName} referred ${toName} for ${kidName}. Tap to Accept or Decline.`,
        payload: {
          kind: 'THERAPIST_REFERRAL',
          referralId: created.id,
          therapistId: input.therapistId,
          therapist2Id: input.therapist2Id,
          kidProfileId: input.kidProfileId,
        },
      });
    } catch (notifyErr) {
      console.warn('Parent referral notification failed', notifyErr);
    }

    return created;
  } catch (e) {
    throw explainGraphqlMissing(e);
  }
}

export async function listReferralsForTherapist(therapistUserId: string): Promise<TherapistReferralRow[]> {
  try {
    const [sent, received] = await Promise.all([
      listReferralsGraphql({ therapistId: { eq: therapistUserId } }),
      listReferralsGraphql({ therapist2Id: { eq: therapistUserId } }),
    ]);
    const byId = new Map<string, TherapistReferralRow>();
    for (const row of [...sent, ...received]) byId.set(row.id, row);
    return Array.from(byId.values()).sort((a, b) =>
      String(b.createdAt || '').localeCompare(String(a.createdAt || '')),
    );
  } catch (e) {
    console.warn('listReferralsForTherapist', e);
    return [];
  }
}

export async function listPendingReferralsForParent(parentId: string): Promise<TherapistReferralRow[]> {
  if (!parentId) return [];
  try {
    const rows = await listReferralsGraphql({
      and: [{ parentId: { eq: parentId } }, { status: { eq: 'PENDING' } }],
    });
    return rows.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  } catch (e) {
    console.warn('listPendingReferralsForParent', e);
    return [];
  }
}

export async function listPendingReferralsForKid(kidProfileId: string): Promise<TherapistReferralRow[]> {
  if (!kidProfileId) return [];
  try {
    const rows = await listReferralsGraphql({
      and: [{ kidProfileId: { eq: kidProfileId } }, { status: { eq: 'PENDING' } }],
    });
    return rows.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  } catch (e) {
    console.warn('listPendingReferralsForKid', e);
    return [];
  }
}

export async function listAcceptedReferralsForKid(kidProfileId: string): Promise<TherapistReferralRow[]> {
  if (!kidProfileId) return [];
  try {
    const rows = await listReferralsGraphql({
      and: [{ kidProfileId: { eq: kidProfileId } }, { status: { eq: 'ACCEPTED' } }],
    });
    return rows.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  } catch (e) {
    console.warn('listAcceptedReferralsForKid', e);
    return [];
  }
}

async function upsertKidTeam(input: {
  kidProfileId: string;
  parentId?: string | null;
  therapistId: string;
  therapist2Id: string;
  note?: string | null;
}): Promise<void> {
  const payload = {
    name: 'Care team',
    kidProfileId: input.kidProfileId,
    adminId: input.parentId || input.therapistId,
    parentId: input.parentId || undefined,
    therapistId: input.therapistId,
    therapist2Id: input.therapist2Id,
    referralNote: input.note?.trim() || undefined,
  };

  // Prefer GraphQL so new Team fields work without refreshed amplify_outputs
  try {
    const listed = await appsyncPublicGraphqlRequest<{
      listTeams?: { items?: Array<{ id?: string | null } | null> | null };
    }>(
      `query ListTeams($filter: ModelTeamFilterInput, $limit: Int) {
        listTeams(filter: $filter, limit: $limit) { items { id } }
      }`,
      { filter: { kidProfileId: { eq: input.kidProfileId } }, limit: 5 },
    );
    const existingId = listed.listTeams?.items?.find((t) => t?.id)?.id;
    if (existingId) {
      await appsyncPublicGraphqlRequest(
        `mutation UpdateTeam($input: UpdateTeamInput!) {
          updateTeam(input: $input) { id }
        }`,
        { input: { id: existingId, ...payload } },
      );
      return;
    }
    await appsyncPublicGraphqlRequest(
      `mutation CreateTeam($input: CreateTeamInput!) {
        createTeam(input: $input) { id }
      }`,
      { input: payload },
    );
    return;
  } catch (gqlErr) {
    console.warn('Team GraphQL upsert failed, trying Amplify client', gqlErr);
  }

  const existing = await listAllByFilter<{ id?: string | null }>(client.models.Team.list as any, {
    kidProfileId: { eq: input.kidProfileId },
  });
  if (existing[0]?.id) {
    const resp = await client.models.Team.update({ id: existing[0].id, ...payload } as any, {
      authMode: 'apiKey',
    });
    assertNoErrors('Team.update', resp.errors);
    return;
  }
  const resp = await client.models.Team.create(payload as any, { authMode: 'apiKey' });
  assertNoErrors('Team.create', resp.errors);
}

async function notifyReferralDecided(
  row: TherapistReferralRow,
  decision: 'ACCEPTED' | 'DECLINED',
  actorUserId?: string,
): Promise<void> {
  const { createNotification } = await import('./notifications');
  const decided = decision === 'ACCEPTED' ? 'accepted' : 'declined';
  const recipients = [row.therapistId, row.therapist2Id].filter(
    (id) => id && id !== actorUserId,
  );
  await Promise.all(
    recipients.map((toUserId) =>
      createNotification({
        toUserId,
        fromUserId: actorUserId,
        organizationId: row.organizationId,
        kidProfileId: row.kidProfileId,
        notificationType: 'REFERRAL_DECIDED',
        title: `Referral ${decided}`,
        body: `The parent ${decided} the therapist referral.`,
        payload: {
          kind: 'REFERRAL_DECIDED',
          referralId: row.id,
          kidProfileId: row.kidProfileId,
        },
        actionUrl: `/client-control/${row.kidProfileId}`,
      }).catch((e) => console.warn('Referral decided notification failed', e)),
    ),
  );
}

export async function acceptTherapistReferral(
  referralId: string,
  actingParentId?: string,
): Promise<TherapistReferralRow> {
  try {
    const row = await getReferralGraphql(referralId);
    if (!row?.id) throw new Error('Referral not found.');
    if (row.status && row.status !== 'PENDING') throw new Error(`Referral is already ${row.status}.`);
    if (actingParentId && row.parentId && row.parentId !== actingParentId) {
      throw new Error("Only this child's parent can accept this referral.");
    }

    const updated = await updateReferralGraphql(referralId, 'ACCEPTED');
    await upsertKidTeam({
      kidProfileId: row.kidProfileId,
      parentId: row.parentId || actingParentId,
      therapistId: row.therapistId,
      therapist2Id: row.therapist2Id,
      note: row.note,
    });
    void import('./careCircleNotifications').then(({ notifyPatientAssigned }) =>
      notifyPatientAssigned({
        kidProfileId: row.kidProfileId,
        newTherapistId: row.therapist2Id,
        actorUserId: actingParentId,
      }),
    );
    void notifyReferralDecided(row, 'ACCEPTED', actingParentId);
    return updated;
  } catch (e) {
    throw explainGraphqlMissing(e);
  }
}

export async function declineTherapistReferral(
  referralId: string,
  actingParentId?: string,
): Promise<void> {
  try {
    if (actingParentId) {
      const row = await getReferralGraphql(referralId);
      if (row?.parentId && row.parentId !== actingParentId) {
        throw new Error("Only this child's parent can decline this referral.");
      }
    }
    await updateReferralGraphql(referralId, 'DECLINED');
    const row = await getReferralGraphql(referralId);
    if (row) void notifyReferralDecided(row, 'DECLINED', actingParentId);
  } catch (e) {
    throw explainGraphqlMissing(e);
  }
}

export async function listKidIdsWhereTherapist2(therapist2Id: string): Promise<string[]> {
  try {
    const data = await appsyncPublicGraphqlRequest<{
      listTeams?: { items?: Array<{ kidProfileId?: string | null } | null> | null };
    }>(
      `query ListTeams($filter: ModelTeamFilterInput, $limit: Int) {
        listTeams(filter: $filter, limit: $limit) { items { kidProfileId } }
      }`,
      { filter: { therapist2Id: { eq: therapist2Id } }, limit: 100 },
    );
    return (data.listTeams?.items || [])
      .map((t) => t?.kidProfileId)
      .filter((id): id is string => Boolean(id));
  } catch {
    try {
      const teams = await listAllByFilter<{ kidProfileId?: string | null }>(client.models.Team.list as any, {
        therapist2Id: { eq: therapist2Id },
      });
      return teams.map((t) => t.kidProfileId).filter((id): id is string => Boolean(id));
    } catch {
      return [];
    }
  }
}
